import React, { useState } from 'react';
import Icon from '../shared/Icon';
import './AutomationWizard.css';
import Step1Type from './steps/Step1Type';
import AlertsAutomationWizard from './AlertsAutomationWizard';
import Step2Sources from './steps/Step2Sources';
import Step3Logic from './steps/Step3Logic';
import Step4Outputs from './steps/Step4Outputs';
import Step5Trigger from './steps/Step5Trigger';
import Step6Review from './steps/Step6Review';
import ReportStepQuery from './steps/ReportStepQuery';
import ReportStepOutput, { isValidReportEmail } from './steps/ReportStepOutput';
import ReportStepSchedule from './steps/ReportStepSchedule';
import ReportStepReview from './steps/ReportStepReview';
import { apiService } from '../../../services/api';
import { defaultScheduleZone } from '../shared/ScheduleZoneField';

const DEFAULT_TRIGGER = { scheduleType: 'scheduled', frequency: 'daily', time: '08:00', dayOfWeek: 'monday' };

function getInitialFormData() {
  return {
    type: '',
    source: null,
    connectionId: null,
    connectionType: null,
    rootPrefix: '',
    path: '',
    datasetAction: 'create',
    datasetId: null,
    datasetName: '',
    createPerFileDataset: false,
    domain: 'default',
    ingestionSettings: {
      fileTypes: [],
      pathPrefix: '',
      filenamePattern: '',
      includeSubfolders: false,
      dedupeStrategy: 'by-path',
      ingestionMode: 'append'
    },
    destinations: [{ kind: 'internal-dataset', targetName: '', connection: 'resai-internal' }],
    trigger: { ...DEFAULT_TRIGGER },
    automationName: '',
    description: '',
    timezone: defaultScheduleZone(),
    reportQuery: '',
    queryName: '',
    reportEmailToSelf: true,
    reportRecipients: [],
    userEmail: '',
    responsePreview: '',
    selectedThreadId: ''
  };
}

// Map a snake_case API row into the camelCase shape the wizard steps consume.
function automationToFormData(a) {
  const base = getInitialFormData();
  if (!a) return base;
  const automationType = (a.automation_type || '').toLowerCase();
  const flowType = automationType === 'reports' || automationType === 'report' ? 'reports' : automationType || 'ingest';
  const trigger = {
    scheduleType: a.schedule_type || 'scheduled',
    frequency: 'daily',
    time: a.schedule_datetime || '08:00',
    dayOfWeek: 'monday',
  };
  return {
    ...base,
    type: flowType,
    source: a.source || null,
    connectionId: a.connection_id ? String(a.connection_id) : null,
    rootPrefix: '',
    path: a.path_prefix || '',
    datasetAction: a.dataset_action || 'create',
    datasetId: a.dataset_id ? String(a.dataset_id) : null,
    datasetName: a.dataset_name || '',
    createPerFileDataset: Boolean(a.create_per_file_dataset),
    trigger,
    automationName: a.name || '',
    description: a.description || '',
    timezone: a.timezone || base.timezone,
    queryName: a.query_name || '',
    reportQuery: a.query_name || '',
    // Preselect the saved query by id (PRD-1269) rather than re-matching by title.
    selectedThreadId: a.report_query_thread_id ? String(a.report_query_thread_id) : '',
    reportEmailToSelf: a.report_email_to_self !== false,
    reportRecipients: Array.isArray(a.report_recipients) ? a.report_recipients : [],
    reportDatasetIds: Array.isArray(a.report_dataset_ids)
      ? a.report_dataset_ids.map(String)
      : [],
  };
}

// camelCase form → snake_case PATCH body for AutomationUpdate.
function formDataToUpdatePayload(formData) {
  const trig = formData.trigger || {};
  const payload = {
    name: (formData.automationName || '').trim() || null,
    description: (formData.description || '').trim() || null,
    schedule_type: trig.scheduleType || null,
    schedule_datetime: trig.time || null,
    timezone: formData.timezone || null,
    path_prefix: (formData.path || '').trim() || null,
    dataset_name: (formData.datasetName || '').trim() || null,
    create_per_file_dataset: !!formData.createPerFileDataset,
  };
  if (formData.type !== 'reports' && formData.connectionId) {
    payload.connection_id = formData.connectionId;
  }
  if (formData.type === 'reports') {
    payload.query_name = (formData.queryName || formData.reportQuery || '').trim() || null;
    payload.report_query_thread_id = formData.selectedThreadId || null;
    payload.report_email_to_self = formData.reportEmailToSelf !== false;
    payload.report_recipients = Array.isArray(formData.reportRecipients) ? formData.reportRecipients : [];
    payload.report_dataset_ids = Array.isArray(formData.reportDatasetIds) ? formData.reportDatasetIds : [];
  }
  // Strip nulls so PATCH honors exclude_unset (null would clear the column).
  return Object.fromEntries(Object.entries(payload).filter(([, v]) => v !== null && v !== undefined));
}

const AutomationWizard = ({ isOpen, onClose, onShowNotification, initialStep, initialReportQuery, initialReportResponse, initialReportThreadId, initialReportDatasetIds, editingAutomation }) => {
  const initialReportQueryTrimmed = initialReportQuery != null && typeof initialReportQuery === 'string' ? initialReportQuery.trim() : '';
  const fromChatWithQuery = initialReportQueryTrimmed.length > 0;
  // Edit mode skips past the locked Type step.
  const isEditMode = Boolean(editingAutomation && editingAutomation.id);

  const [currentStep, setCurrentStep] = useState(() => {
    if (isEditMode) return 2;
    const step = fromChatWithQuery
      ? Math.min(4, Math.max(1, Number(initialStep) || 2))
      : Math.min(4, Math.max(1, Number(initialStep) || 1));
    return step;
  });
  const [formData, setFormData] = useState(() => {
    if (isEditMode) {
      return automationToFormData(editingAutomation);
    }
    const base = getInitialFormData();
    if (fromChatWithQuery) {
      const responsePreview = initialReportResponse != null && typeof initialReportResponse === 'string' ? initialReportResponse.trim() : '';
      const selectedThreadId = initialReportThreadId != null && initialReportThreadId !== '' ? String(initialReportThreadId) : '';
      return {
        ...base,
        type: 'reports',
        reportQuery: initialReportQueryTrimmed,
        queryName: initialReportQueryTrimmed,
        ...(responsePreview && { responsePreview }),
        ...(selectedThreadId && { selectedThreadId }),
        threadDatasetIds: Array.isArray(initialReportDatasetIds) ? initialReportDatasetIds : [],
      };
    }
    return base;
  });

  // Alerts has its own wizard, cloned from the Reports pattern. The generic
  // builder hands over rather than continuing into six steps that describe a
  // different kind of automation.
  //
  // Gated on `currentStep > 1`, so the handoff happens on **Next** and not on
  // the card click. `Step1Type.handleTypeSelect` only sets `formData.type`, so
  // without this gate clicking the Alerts card instantly unmounted this wizard
  // mid-selection — a mis-click the author could not undo.
  const isAlertsFlow = formData.type === 'alerts' && currentStep > 1;

  // For Ingest and Reports, use 4 steps; for others, use 6 steps
  const isIngestFlow = formData.type === 'ingest';
  const isReportsFlow = formData.type === 'reports';
  const totalSteps = isReportsFlow || isIngestFlow ? 4 : 6;

  const getSteps = () => {
    if (isIngestFlow) {
      return [
        { number: 1, label: 'Type' },
        { number: 2, label: 'Sources' },
        { number: 3, label: 'Target dataset' },
        { number: 4, label: 'Schedule and review' },
      ];
    }
    if (isReportsFlow) {
      return [
        { number: 1, label: 'Type' },
        { number: 2, label: 'Query' },
        { number: 3, label: 'Output' },
        { number: 4, label: 'Schedule and Review' },
      ];
    }
    return [
      { number: 1, label: 'Type' },
      { number: 2, label: 'Sources' },
      { number: 3, label: 'Logic' },
      { number: 4, label: 'Outputs' },
      { number: 5, label: 'Trigger' },
      { number: 6, label: 'Review' },
    ];
  };

  const steps = getSteps();

  const handleNext = () => {
    // On the report Output step (reports flow, step 3), validate/commit any
    // address typed into "Email To Others" but not yet added, so a bad address
    // is caught before advancing rather than silently ignored (PRD-1269).
    if (isReportsFlow && currentStep === 3) {
      const pending = (formData.pendingRecipient || '').trim();
      if (pending) {
        if (!isValidReportEmail(pending)) {
          setFormData((f) => ({ ...f, recipientError: 'Enter a valid email address (e.g. name@example.com).' }));
          return;
        }
        setFormData((f) => {
          const recips = f.reportRecipients ?? [];
          return {
            ...f,
            reportRecipients: recips.includes(pending) ? recips : [...recips, pending],
            pendingRecipient: '',
            recipientError: '',
          };
        });
      }
    }
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleClose = () => {
    setCurrentStep(1);
    setFormData(getInitialFormData());
    onClose();
  };

  // When props change after open (e.g. "Schedule as report" from chat), sync to reports flow and step 2
  React.useEffect(() => {
    if (!isOpen) return;
    const q = initialReportQuery != null && typeof initialReportQuery === 'string' ? initialReportQuery.trim() : '';
    if (q) {
      const step = Math.min(4, Math.max(1, Number(initialStep) || 2));
      const responsePreview = initialReportResponse != null && typeof initialReportResponse === 'string' ? initialReportResponse.trim() : '';
      const selectedThreadId = initialReportThreadId != null && initialReportThreadId !== '' ? String(initialReportThreadId) : '';
      setFormData((prev) => ({
        ...prev,
        type: 'reports',
        reportQuery: q,
        queryName: q,
        ...(responsePreview && { responsePreview }),
        ...(selectedThreadId && { selectedThreadId }),
        threadDatasetIds: Array.isArray(initialReportDatasetIds) ? initialReportDatasetIds : [],
      }));
      setCurrentStep(step);
    }
  }, [isOpen, initialReportQuery, initialStep, initialReportResponse, initialReportThreadId, initialReportDatasetIds]);

  // Load current user email when Reports is selected (for "Email to self" display)
  React.useEffect(() => {
    if (!isOpen || formData.type !== 'reports') return;
    let cancelled = false;
    apiService.getCurrentUser().then((user) => {
      if (cancelled || !user) return;
      const email = (user.email || (user.name && user.name.includes('@') ? user.name : null) || '').trim();
      if (email) {
        setFormData((prev) => ({ ...prev, userEmail: email }));
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [isOpen, formData.type]);

  const handleCreate = async () => {
    try {
      if (isEditMode) {
        const patch = formDataToUpdatePayload(formData);
        await apiService.updateAutomation(editingAutomation.id, patch);
        if (onShowNotification) {
          onShowNotification('Automation updated', 'success');
        }
        handleClose();
        return;
      }
      if (formData.type === 'reports') {
        const dsIds = formData.reportDatasetIds ?? [];
        const rawQuery = formData.queryName || formData.reportQuery || '';
        const cleanQuery = rawQuery.replace(/^\[Datasets?:.*\]\s*/i, '').replace(/^\[Files?: .+\]\s*/i, '').trim() || rawQuery;
        const payload = {
          type: 'reports',
          connectionId: formData.connectionId,
          datasetAction: 'update',
          trigger: { ...DEFAULT_TRIGGER, ...(formData.trigger || {}) },
          automationName: formData.automationName || cleanQuery.slice(0, 50) || 'Report',
          queryName: cleanQuery,
          // Bind the report to the source saved query by id (PRD-1269) so it can't
          // be matched to the wrong query by title, and so the delivered email can
          // deep-link back to it.
          reportQueryThreadId: formData.selectedThreadId || null,
          description: formData.description || null,
          timezone: formData.timezone || defaultScheduleZone(),
          reportEmailToSelf: formData.reportEmailToSelf !== false,
          reportRecipients: Array.isArray(formData.reportRecipients) ? formData.reportRecipients : [],
          reportDatasetIds: dsIds,
        };
        await apiService.createAutomation(payload);
        if (onShowNotification) {
          onShowNotification('Report automation created successfully!', 'success');
        }
      } else {
        await apiService.createAutomation(formData);
        if (onShowNotification) {
          onShowNotification('Automation created successfully!', 'success');
        }
      }
      handleClose();
    } catch (error) {
      console.error('Error saving automation:', error);
      if (onShowNotification) {
        const verb = isEditMode ? 'update' : 'create';
        onShowNotification(error.message || `Failed to ${verb} automation`, 'error');
      }
    }
  };

  const renderStepContent = () => {
    if (isReportsFlow) {
      switch (currentStep) {
        case 1:
          return <Step1Type formData={formData} onSetFormData={setFormData} />;
        case 2:
          return <ReportStepQuery formData={formData} onSetFormData={setFormData} />;
        case 3:
          return <ReportStepOutput formData={formData} onSetFormData={setFormData} />;
        case 4:
          return (
            <>
              <ReportStepSchedule formData={formData} onSetFormData={setFormData} />
              <ReportStepReview
                formData={formData}
                onSetFormData={setFormData}
                onCreate={handleCreate}
                showCreateButton={false}
              />
            </>
          );
        default:
          return null;
      }
    }
    if (isIngestFlow) {
      // Ingest flow: 4 steps (Type + 3)
      switch (currentStep) {
        case 1:
          return <Step1Type formData={formData} onSetFormData={setFormData} />;
        case 2:
          return <Step2Sources formData={formData} onSetFormData={setFormData} />;
        case 3:
          return <Step4Outputs formData={formData} onSetFormData={setFormData} />;
        case 4:
          return <Step5Trigger formData={formData} onSetFormData={setFormData} />;
        default:
          return null;
      }
    }
    // Other flows: 6 steps
    switch (currentStep) {
      case 1:
        return <Step1Type formData={formData} onSetFormData={setFormData} />;
      case 2:
        return <Step2Sources formData={formData} onSetFormData={setFormData} />;
      case 3:
        return <Step3Logic formData={formData} onSetFormData={setFormData} />;
      case 4:
        return <Step4Outputs formData={formData} onSetFormData={setFormData} />;
      case 5:
        return <Step5Trigger formData={formData} onSetFormData={setFormData} />;
      case 6:
        return <Step6Review formData={formData} onSetFormData={setFormData} onCreate={handleCreate} />;
      default:
        return null;
    }
  };

  const canProceed = () => {
    if (isReportsFlow) {
      switch (currentStep) {
        case 1:
          return formData.type === 'reports';
        case 2:
          return !!(formData.reportQuery || formData.queryName);
        case 3:
          return true;
        case 4:
          return !!(formData.automationName && formData.automationName.trim() && (formData.queryName || formData.reportQuery || '').trim());
        default:
          return false;
      }
    }
    if (isIngestFlow) {
      switch (currentStep) {
        case 1:
          return formData.type === 'ingest';
        case 2:
          return formData.source !== null && formData.connectionId !== null;
        case 3:
          if (formData.datasetAction === 'create') {
            // Per-file mode doesn't require a single dataset name — each file becomes its own.
            return formData.createPerFileDataset || formData.datasetName !== '';
          } else {
            return formData.datasetId !== null;
          }
        case 4:
          return formData.automationName !== '';
        default:
          return false;
      }
    }
    switch (currentStep) {
      case 1:
        return formData.type !== '';
      case 2:
        return formData.source !== null;
      case 3:
        return true;
      case 4:
        return formData.destinations.length > 0 && formData.destinations[0].targetName !== '';
      case 5:
        return true;
      case 6:
        return formData.automationName !== '';
      default:
        return false;
    }
  };

  if (!isOpen) return null;

  // Hand off to the Alerts wizard once the type step selects Alerts. Not an
  // edit flow — an existing alert is edited from its own modal, and this
  // branch only fires for a new automation whose template is Alerts.
  //
  // The handoff starts the Alerts wizard on its own empty form, which is safe
  // only because nothing user-entered exists yet at this point: the two paths
  // that seed real formData are edit mode (excluded above) and the chat's
  // "Schedule as report" (which forces type 'reports'), so reaching here means
  // formData is getInitialFormData() plus the chosen type. **If the type step
  // ever collects a field, that field has to be passed through here** or it
  // will be silently dropped.
  if (isAlertsFlow && !isEditMode) {
    return (
      <AlertsAutomationWizard
        isOpen={isOpen}
        onClose={onClose}
        onShowNotification={onShowNotification}
        initialStep={2}
        // Hands control back to this builder's type step, so choosing Alerts
        // is reversible without cancelling the whole wizard.
        onChooseDifferentTemplate={() => {
          setFormData((prev) => ({ ...prev, type: '' }));
          setCurrentStep(1);
        }}
      />
    );
  }

  return (
    <div className="automation-wizard-page">
      <div className="automation-wizard-container">
        <div className="wizard-header">
          <div className="wizard-breadcrumb">
            <span onClick={handleClose}>Automations</span>
            <span className="wizard-breadcrumb-separator">/</span>
            <span>{isEditMode ? `Edit: ${editingAutomation?.name || 'Automation'}` : 'New automation'}</span>
          </div>
          <div className="wizard-header-content">
            <h2 className="wizard-title">{isEditMode ? 'Edit automation' : 'New automation'}</h2>
            <button className="wizard-close" onClick={handleClose}>
              Cancel
            </button>
          </div>
        </div>

        {currentStep > 1 && (
          <div className="wizard-steps">
            {steps.map((step) => (
              <div
                key={step.number}
                className={`wizard-step ${currentStep === step.number ? 'wizard-step-active' : ''} ${currentStep > step.number ? 'wizard-step-completed' : ''}`}
              >
                <span className="wizard-step-number">{step.number}.</span>
                <span className="wizard-step-label">{step.label}</span>
              </div>
            ))}
          </div>
        )}

        {isEditMode && (
          <div className="resui-banner resui-banner--alert" style={{ margin: 'var(--p-3) var(--p-6) 0' }}>
            <Icon name="warning" />
            <span className="resui-banner-text">
              <strong>Editing an existing automation.</strong>{' '}
              {formData.type === 'reports' ? (
                <>You can update the name, description, schedule, recipients, datasets, and query.</>
              ) : (
                <>
                  You can update the name, description, schedule, target dataset name, and source
                  connection. Switching connection resets the incremental fetch cursor — the next
                  run starts fresh.
                </>
              )}
            </span>
          </div>
        )}

        <div className="wizard-content">
          {renderStepContent()}
        </div>

        <div className="wizard-footer">
          {currentStep > 1 && (
            <button className="wizard-button wizard-button-secondary" onClick={handleBack}>
              Back
            </button>
          )}
          {currentStep === totalSteps ? (
            <button
              className="wizard-button wizard-button-primary"
              onClick={handleCreate}
              disabled={!canProceed()}
            >
              {isEditMode ? 'Save changes' : 'Create automation'}
            </button>
          ) : (
            <button
              className="wizard-button wizard-button-primary"
              onClick={handleNext}
              disabled={!canProceed()}
            >
              Next
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AutomationWizard;
